import { f0FormField, RenderIfCondition } from '@factorialco/f0-react'
import { F0SelectItemObject } from '@factorialco/f0-react'
import { z } from 'zod'

import { LegalEntityRecord, useLegalEntitySelectorSource } from './source'

type BaseLegalEntitySelectorFormFieldParams = {
  label: string
  section: string
  placeholder?: string
  helpText?: string
  renderIf?: RenderIfCondition
}

const mapLegalEntityOptions = (
  legalEntity: LegalEntityRecord
): F0SelectItemObject<string, LegalEntityRecord> => ({
  value: legalEntity.id,
  label: legalEntity.legalName,
  item: legalEntity,
})

// Single select - optional
const useOptionalSingleLegalEntitySelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLegalEntitySelectorFormFieldParams) => {
  const schema = z.string().optional()

  const source = useLegalEntitySelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    showSearchBox: true,
    source,
    mapOptions: mapLegalEntityOptions,
  })
}

// Single select - mandatory
const useMandatorySingleLegalEntitySelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLegalEntitySelectorFormFieldParams) => {
  const schema = z.string()

  const source = useLegalEntitySelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    showSearchBox: true,
    source,
    mapOptions: mapLegalEntityOptions,
  })
}

// Multi select - optional
const useOptionalMultiLegalEntitySelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLegalEntitySelectorFormFieldParams) => {
  const schema = z.array(z.string()).optional()

  const source = useLegalEntitySelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    multiple: true,
    showSearchBox: true,
    source,
    mapOptions: mapLegalEntityOptions,
  })
}

// Multi select - mandatory
const useMandatoryMultiLegalEntitySelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLegalEntitySelectorFormFieldParams) => {
  const schema = z.array(z.string()).min(1)

  const source = useLegalEntitySelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    multiple: true,
    showSearchBox: true,
    source,
    mapOptions: mapLegalEntityOptions,
  })
}

type SingleLegalEntitySelectorFormFieldParams<M extends boolean> =
  BaseLegalEntitySelectorFormFieldParams & { mandatory: M }

export const useSingleLegalEntitySelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: SingleLegalEntitySelectorFormFieldParams<M>) => {
  const mandatorySingleLegalEntitySelectorFormField =
    useMandatorySingleLegalEntitySelectorFormField({ ...rest, renderIf })
  const optionalSingleLegalEntitySelectorFormField = useOptionalSingleLegalEntitySelectorFormField({
    ...rest,
    renderIf,
  })

  return mandatory
    ? mandatorySingleLegalEntitySelectorFormField
    : optionalSingleLegalEntitySelectorFormField
}

type MultiLegalEntitySelectorFormFieldParams<M extends boolean> =
  BaseLegalEntitySelectorFormFieldParams & { mandatory: M }

export const useMultiLegalEntitySelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: MultiLegalEntitySelectorFormFieldParams<M>) => {
  const mandatoryMultiLegalEntitySelectorFormField = useMandatoryMultiLegalEntitySelectorFormField({
    ...rest,
    renderIf,
  })
  const optionalMultiLegalEntitySelectorFormField = useOptionalMultiLegalEntitySelectorFormField({
    ...rest,
    renderIf,
  })

  return mandatory
    ? mandatoryMultiLegalEntitySelectorFormField
    : optionalMultiLegalEntitySelectorFormField
}
